import { useCallback, useContext, useState, useRef } from 'react'
import { css, keyframes } from '@emotion/react'
import { t } from '@lingui/core/macro'
import FilterListIcon from '@mui/icons-material/FilterList'
import InfoIcon from '@mui/icons-material/Info'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Draggable from 'react-draggable'
import { useSelector } from 'react-redux'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { colors } from '@global/constants'
import { ColumnFilterPopover } from '../columnFilterPopover'
import styles from './ReadGridHeader.css'
import { ReadGridHeaderIcon } from './ReadGridHeaderIcon'
import { ReadGridHeaderFilters } from '../../../../contexts/readGridHeaderFilters'
import { ReadGridColumnWidthContext } from '../context/column-width-context'
import { rowPropertyAliases } from '../readGridToolbar/components/toggleColumns/config'

const ColumnFilterButtonStyles = {
  minWidth: 0,
  padding: 0,
  float: 'right',
  borderRadius: '12%',
  '&:hover': {
    color: colors.white,
    backgroundColor: colors.blue.main,
    boxShadow:
      '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
  },
}

const FloatedVisibiltyIconStyles = {
  color: '#c3c3c3',
  float: 'right',
  fontSize: 18,
}

const infoIcon = css`
  margin-left: 4px;
`

const pulse = keyframes`
  0% {
    background-color: var(--table-th-bgcolor);
    box-shadow: 0 0 0 0.5px var(--table-border-color) inset;
  }
  50% {
    background-color: var(--table-active-highlight-even-td-bgColor);
    box-shadow: 0 0 0 2px var(--dataTableCell-firstSelected) inset;
  }
  100% {
    background-color: var(--table-th-bgcolor);
    box-shadow: 0 0 0 0.5px var(--table-border-color) inset;
  }
`

const headerStyles = css`
  &.-go-to-pulse {
    background-color: var(--table-th-bgcolor);
    box-shadow: 0 0 0 0.5px var(--table-border-color) inset;
    animation-name: ${pulse};
    animation-duration: 1.5s;
    animation-iteration-count: 2;
  }
`

export function ReadGridHeader({
  column,
  columnIndex,
  parent,
  style,
  userOptions,
  sortOrderString,
}) {
  const DATA_TYPE_TEXT = {
    cv_id: '',
    list: t`List`,
    date: t`Date`,
    'link-validation': t`Validation`,
    link: t`Link`,
    'shortened link': t`Shortened link`,
    'qr-code': t`QR code`,
    'deep-link': t`Deep link`,
    'custom-bitlink': t`Custom Bitly link`,
    autofill: t`Autofill`,
    autonumber: t`Number`,
    random: t`Text`,
    pattern: t`Pattern`,
    concatenation: t`Concatenation`,
    property: t`Property`,
    creative: t`Content comprehension`,
  }

  const queryContext = useDatasetQueryContext()
  const columnWidthContext = useContext(ReadGridColumnWidthContext)
  const popoverAnchorEl = useRef(null)
  const [filterPopoverOpened, setFilterPopoverOpened] = useState(false)
  const openFilterPopover = useCallback(() => {
    setFilterPopoverOpened(true)
  }, [setFilterPopoverOpened])
  const closeFilterPopover = useCallback(() => {
    setFilterPopoverOpened(false)
  }, [setFilterPopoverOpened])
  const canSeeDatasetQueryOptions = useSelector(
    state => state.Auth.features['dataset-query-options'],
  )

  const onDrag = useCallback(
    (event, { deltaX }) => {
      columnWidthContext.updateColumnWidth(columnIndex, deltaX)
      parent.recomputeGridSize({ columnIndex, rowIndex: 0 })
    },
    [columnWidthContext],
  )

  const showHeaderFilter = useContext(ReadGridHeaderFilters)?.showHeaderFilter

  const governanceType = column?.governanceType
  const rowPropertyName = rowPropertyAliases.get(column?.rowPropertyName)?.display
  const dbColumnName = rowPropertyAliases.get(column?.rowPropertyName)?.db || column.fieldId
  const filterButtonColor = queryContext?.hasQueryOptions(dbColumnName)
    ? colors.white
    : colors.gray.medium
  const filterButtonVariant = queryContext?.hasQueryOptions(dbColumnName) ? 'contained' : 'text'
  const iconText = column ? DATA_TYPE_TEXT[governanceType] || t`Text` : ''

  const showColumnFilter =
    canSeeDatasetQueryOptions && queryContext && showHeaderFilter && !column?.isClassificationColumn
  const isRowProperty = governanceType === 'property'
  const showHiddenIcon = !column.visible || column.defaultHidden

  return (
    <div
      className={`read-grid-cell ${styles.columnHeader} ${styles.readGridCellPulse}`}
      style={style}
      css={headerStyles}
      data-read-grid-header-name={column.name}
      data-read-grid-header-index={columnIndex}
    >
      {showColumnFilter && (
        <>
          <Button
            ref={popoverAnchorEl}
            sx={{
              ...ColumnFilterButtonStyles,
              color: filterButtonColor,
            }}
            onClick={openFilterPopover}
            variant={filterButtonVariant}
            disableElevation
            data-testid="filter-button"
          >
            <FilterListIcon fontSize="small" />
          </Button>
          {filterPopoverOpened && (
            <ColumnFilterPopover
              anchorEl={popoverAnchorEl.current}
              onClose={closeFilterPopover}
              fieldId={dbColumnName}
              governanceType={governanceType}
              rowPropertyName={rowPropertyName}
              fieldType={iconText}
              sortOrder={sortOrderString}
              readGridParent={parent}
              userOptions={userOptions}
            />
          )}
        </>
      )}
      <div className={styles.row1}>
        {column.required && <span className={styles.required}>*</span>}
        {column.name}
      </div>

      <div className={styles.row2}>
        <div className={styles.iconContainer}>
          {column && !isRowProperty && <ReadGridHeaderIcon governanceType={governanceType} />}
        </div>
        <div className={styles.iconText}>{iconText}</div>
        <div className={styles.spacer} />
        <div className={styles.extraContainer}>
          {showHiddenIcon && (
            <Tooltip title={t`Default hidden field`}>
              <VisibilityOffIcon sx={FloatedVisibiltyIconStyles} />
            </Tooltip>
          )}
          {column?.description && (
            <Tooltip title={column.description}>
              <InfoIcon fontSize="small" css={infoIcon} />
            </Tooltip>
          )}
        </div>
      </div>

      <Draggable
        axis="x"
        defaultClassName="DragHandle"
        defaultClassNameDragging={styles.active}
        onDrag={onDrag}
        position={{ x: 0 }}
        zIndex={999}
      >
        <div className={styles.dragHandle} />
      </Draggable>
    </div>
  )
}
